import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { openTillCart } from '@/lib/api/till';
import { cartAttributes, track } from '@/lib/measurement';
import { STORAGE_KEYS, storage } from '@/lib/storage/keys';
import {
  defaultShipCountry,
  parseStoredLines,
  quantityOf,
  resolveCart,
  sellableLines,
  withAdded,
  withQuantity,
  type Cart,
  type StoredLine,
} from '@/lib/store';

export interface StoreValue {
  /** The visitor's cart; null while it holds nothing. */
  readonly cart: Cart | null;
  readonly isCartOpen: boolean;
  /** True while checkout is being opened. */
  readonly busy: boolean;
  setCartOpen: (open: boolean) => void;
  /**
   * Adds a line and opens the drawer. Resolves true when the cart holds more of the variant than
   * before, false when it could not take it (unknown, unavailable, or at the per-line limit).
   */
  addToCart: (variantId: string, quantity?: number) => Promise<boolean>;
  updateCartItem: (lineId: string, quantity: number) => Promise<void>;
  removeFromCart: (lineId: string) => Promise<void>;
  /** The country the order ships to (the shipping is priced for it). */
  readonly shipCountry: string;
  setShipCountry: (country: string) => void;
  /** Why checkout could not be opened, shown in the drawer; null otherwise. */
  readonly checkoutError: string | null;
  /** Same-tab Stripe checkout for the current cart (no-op for an empty cart). */
  openCheckout: () => void;
}

const StoreContext = createContext<StoreValue | null>(null);

function readLines(): StoredLine[] {
  return parseStoredLines(storage.get(STORAGE_KEYS.cartLines));
}

/**
 * The site's own cart (ΩΞ-TILL-MERCH-001). Lines live in the browser and are priced from the gear
 * catalogue whenever they are shown; checkout is the till (Stripe), which prices them again from
 * the same file. The static /cart page edits the same stored lines; this provider follows it.
 */
export function StoreProvider({ children }: { readonly children: ReactNode }) {
  const [lines, setLines] = useState<StoredLine[]>(readLines);
  const [isCartOpen, setCartOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [shipCountry, setShipCountryState] = useState(() =>
    defaultShipCountry(
      storage.get(STORAGE_KEYS.shipCountry),
      typeof navigator === 'undefined' ? [] : navigator.languages,
    ),
  );

  const write = useCallback((next: StoredLine[]) => {
    const kept = sellableLines(next);
    setLines(kept);
    setCheckoutError(null);
    if (kept.length === 0) storage.remove(STORAGE_KEYS.cartLines);
    else storage.set(STORAGE_KEYS.cartLines, JSON.stringify(kept));
  }, []);

  useEffect(() => {
    const follow = (event: StorageEvent) => {
      if (event.key === STORAGE_KEYS.cartLines) setLines(readLines());
    };
    window.addEventListener('storage', follow);
    return () => {
      window.removeEventListener('storage', follow);
    };
  }, []);

  const addToCart = useCallback(
    (variantId: string, quantity = 1): Promise<boolean> => {
      const current = readLines();
      const before = quantityOf(current, variantId);
      const next = sellableLines(withAdded(current, variantId, quantity));
      write(next);
      const added = quantityOf(next, variantId) > before;
      if (added) setCartOpen(true);
      return Promise.resolve(added);
    },
    [write],
  );

  const updateCartItem = useCallback(
    (lineId: string, quantity: number) => {
      write(withQuantity(readLines(), lineId, quantity));
      return Promise.resolve();
    },
    [write],
  );

  const removeFromCart = useCallback(
    (lineId: string) => {
      write(withQuantity(readLines(), lineId, 0));
      return Promise.resolve();
    },
    [write],
  );

  const setShipCountry = useCallback((country: string) => {
    setShipCountryState(country);
    setCheckoutError(null);
    storage.set(STORAGE_KEYS.shipCountry, country);
  }, []);

  const cart = useMemo<Cart | null>(() => {
    const resolved = resolveCart(lines);
    return resolved.lines.length === 0 ? null : resolved;
  }, [lines]);

  const openCheckout = useCallback(() => {
    if (cart === null || busy) return;
    setBusy(true);
    setCheckoutError(null);
    try {
      track('begin_checkout', { value: cart.subtotal, currency: 'USD', items: cart.totalQuantity });
    } catch {
      /* measurement never blocks checkout */
    }
    let attribution: ReturnType<typeof cartAttributes> = [];
    try {
      attribution = cartAttributes();
    } catch {
      /* attribution never stands between a buyer and the payment page */
    }
    openTillCart(
      cart.lines.map((line) => ({ id: line.variantId, qty: line.quantity })),
      shipCountry,
      window.location.pathname,
      attribution,
    )
      .then((url) => {
        window.location.assign(url);
      })
      .catch((error: unknown) => {
        setCheckoutError(error instanceof Error && error.message !== '' ? error.message : 'Could not open checkout.');
        setBusy(false);
      });
  }, [busy, cart, shipCountry]);

  const value = useMemo<StoreValue>(
    () => ({
      cart,
      isCartOpen,
      busy,
      setCartOpen,
      addToCart,
      updateCartItem,
      removeFromCart,
      shipCountry,
      setShipCountry,
      checkoutError,
      openCheckout,
    }),
    [
      cart,
      isCartOpen,
      busy,
      addToCart,
      updateCartItem,
      removeFromCart,
      shipCountry,
      setShipCountry,
      checkoutError,
      openCheckout,
    ],
  );

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

// The hook lives beside its provider because this path is the public contract for both.
// eslint-disable-next-line react-refresh/only-export-components
export function useStore(): StoreValue {
  const value = useContext(StoreContext);
  if (value === null) throw new Error('useStore must be used within StoreProvider');
  return value;
}
